import React, { useCallback, useEffect, useState } from 'react';
import { useStringsContext } from '../../../../../contexts/strings';
import PollCard from '../poll-card';
import { QuizPoll, QuizQuestion, QuizQuestionType } from '../../types';
import { InputWithValidateProps } from '../../../../common/input/text-input-with-validate';
import AccordionWrapper from '../../../../common/accordion-wrapper';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import PollQuizTextAlternatives from '../poll-quiz-text-alternatives';
import PollQuizQuestion from '../poll-quiz-question';
import { SxStyle } from '../../../../../types';
import * as yup from 'yup';
import svStrings from '../../../../../sv-strings';
import Box from '@mui/material/Box';
import PollQuizPlayerAlternatives from '../poll-quiz-player-alternatives';
import ImageWithValidate from '../../../../common/input/image-with-validate';

const styles: SxStyle = {
  card: {
    border: 'none',
    padding: 0,
    minHeight: '600px',
  },
  content: {
    paddingLeft: '2px',
    paddingRight: 2,
  },
};

const defaultQuizQuestion: QuizQuestion = {
  text: undefined,
  answer: 0,
  timelimit: 0,
  type: 'text',
};

const questionImageValidation = yup.string().url(svStrings.validations.validImage).nullable(true);

type QuizQuestionsKey = keyof Pick<QuizPoll, 'questions'>;
type QuestionTextAlternativesKey = keyof Pick<QuizQuestion, 'textAlternatives'>;
type QuestionPlayerAlternativesKey = keyof Pick<QuizQuestion, 'playerAlternatives'>;
type QuestionAnswerKey = keyof Pick<QuizQuestion, 'answer'>;
type QuestionImageKey = keyof Pick<QuizQuestion, 'backgroundImage'>;

const quizQuestionsKey: QuizQuestionsKey = 'questions';
const questionImageKey: QuestionImageKey = 'backgroundImage';
const questionTextAlternativesTextKey = 'textAlternatives';
const questionAlternativesPlayerKey = 'playerAlternatives';

interface Props extends InputWithValidateProps {
  onValue: (key: string, value: any | null) => void;
  selectedAbbrv: string;
  selectedPoll: QuizPoll | null;
  pollId: string;
  isEditing: boolean;
}

const PollQuizCard = ({
  isEditing,
  pollId,
  selectedAbbrv,
  selectedPoll,
  onValue,
  onValidation,
  onRemoveValue,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();

  const [expandedAccordion, setExpandedAccordion] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>(
    isEditing && selectedPoll?.questions
      ? [...(selectedPoll.questions ? selectedPoll.questions : [])]
      : [{ ...defaultQuizQuestion }]
  );

  useEffect(() => {
    onValue && onValue(quizQuestionsKey, quizQuestions);
  }, [onValue, quizQuestions]);

  const handleQuestionValue = useCallback(
    (key: QuizQuestionsKey, updatedQuestion: QuizQuestion, questionIndex: number) => {
      const updatedQuestions = quizQuestions.map((q, index) =>
        index === questionIndex ? updatedQuestion : q
      );
      setQuizQuestions(updatedQuestions);
    },
    [quizQuestions]
  );

  const handleAlternativesValue = useCallback(
    (
      key: QuestionTextAlternativesKey | QuestionPlayerAlternativesKey | QuestionAnswerKey,
      value: any,
      question: QuizQuestion,
      questionIndex: number
    ) => {
      question[key] = value;
      const updatedQuestions = quizQuestions.map((q, index) =>
        index === questionIndex ? question : q
      );
      setQuizQuestions(updatedQuestions);
    },
    [quizQuestions]
  );

  const handleImageValue = useCallback(
    (key: QuestionImageKey, value: any, question: QuizQuestion, questionIndex: number) => {
      question[key] = value;
      const updatedQuestions = quizQuestions.map((q, index) =>
        index === questionIndex ? question : q
      );
      setQuizQuestions(updatedQuestions);
    },
    [quizQuestions]
  );

  const onAddQuestion = useCallback(() => {
    setQuizQuestions((prev) => {
      return [...prev, { ...defaultQuizQuestion }];
    });
    setExpandedAccordion(quizQuestions.length);
  }, [quizQuestions]);

  const onExpandAccordion = useCallback((index: number, expanded: boolean) => {
    setExpandedAccordion(index);
  }, []);

  const onDeleteQuestion = useCallback(
    (questionIndex: number) => {
      setExpandedAccordion(quizQuestions.length - 2);
      quizQuestions.splice(questionIndex, 1);
      setQuizQuestions([...quizQuestions]);
    },
    [quizQuestions]
  );

  const handleChangeType = useCallback(
    (type: QuizQuestionType, question: QuizQuestion, questionIndex: number) => {
      // Remove previous alternatives if type changes
      if (type === 'player') {
        delete question.textAlternatives;
        question = { ...defaultQuizQuestion, type: 'player' };
        onRemoveValue && onRemoveValue(questionTextAlternativesTextKey);
      } else {
        delete question.playerAlternatives;
        question = { ...defaultQuizQuestion, type: 'text' };
        onRemoveValue && onRemoveValue(questionAlternativesPlayerKey);
      }
      const updatedQuestions = quizQuestions.map((q, index) =>
        index === questionIndex ? question : q
      );
      setQuizQuestions(updatedQuestions);
    },
    [onRemoveValue, quizQuestions]
  );

  return (
    <PollCard cardStyle={styles.card}>
      {quizQuestions.map((question, index) => (
        <AccordionWrapper
          key={index}
          title={strings.polls.quizQuestionTitle + String(index + 1)}
          expanded={expandedAccordion === index}
          onChange={(event, expanded) => onExpandAccordion(index, expanded)}
        >
          <Stack spacing={4} sx={styles.content}>
            <PollQuizQuestion
              selectedQuestion={question}
              onValue={(key, value) => handleQuestionValue(key, value, index)}
              onValidation={onValidation}
              onChangeType={(key) => handleChangeType(key, question, index)}
              hideValidationError={hideValidationError}
            />
            {question.type === 'text' ? (
              <PollQuizTextAlternatives
                selectedQuestion={question}
                isEditing={isEditing}
                onValue={(key, value) => handleAlternativesValue(key, value, question, index)}
                hideValidationError={hideValidationError}
              />
            ) : (
              <PollQuizPlayerAlternatives
                selectedQuestion={question}
                selectedAbbrv={selectedAbbrv}
                onValue={(key, value) => handleAlternativesValue(key, value, question, index)}
                hideValidationError={hideValidationError}
              />
            )}
            <Box>
              <Stack>
                <Typography variant="body2-bold">
                  {strings.polls.questionBackgroundmageTitle}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {strings.polls.questionBackgroundmageLabel1}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {strings.polls.questionBackgroundmageLabel2}
                </Typography>
              </Stack>
              <ImageWithValidate
                name={questionImageKey}
                storagePath={`pollImages/${pollId}`}
                onValue={(key, value) => handleImageValue(questionImageKey, value, question, index)}
                onValidation={onValidation}
                value={question.backgroundImage || ''}
                hideValidationError={hideValidationError}
                validation={questionImageValidation}
              />
            </Box>
            {index > 0 && (
              <Button
                startIcon={<DeleteIcon />}
                variant="outlined"
                color="error"
                onClick={() => onDeleteQuestion(index)}
              >
                {strings.polls.quizRemoveQuestion}
              </Button>
            )}
          </Stack>
        </AccordionWrapper>
      ))}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddQuestion}>
        {strings.polls.quizQuestionAdd + String(quizQuestions.length + 1)}
      </Button>
    </PollCard>
  );
};

export default PollQuizCard;
